/**
 * <product-form> — ports app/components/ProductForm.tsx interactivity to a
 * vanilla custom element. On option-button click it resolves the variant that
 * matches the new selection set, then:
 *   - marks the chosen option button .is-selected (per group),
 *   - rewrites ?variant= via history.replaceState (no scroll/navigation, like
 *     useSelectedOptionInUrlParam + navigate({replace, preventScrollReset})),
 *   - updates the price line, the add-to-cart hidden id + label + disabled state,
 *     and re-emits the price for the sticky mobile bar.
 *
 * Pre-order rules mirror ProductForm/PdpMobileBar: the reserve label/enabled
 * state come from data-* on the host (resolved server-side via the three-tier
 * casa_release fallback). A pre-order piece stays reservable even with no live
 * stock; a non-pre-order piece falls back to Add to cart / Sold out (disabled).
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.variants = this.#readVariants();
    this.preorder = this.getAttribute('data-preorder') === 'true';
    this.labelReserve = this.getAttribute('data-label-reserve') || 'Reserve Your Piece';
    this.labelAdd = this.getAttribute('data-label-add') || 'Add to cart';
    this.labelSoldOut = this.getAttribute('data-label-soldout') || 'Sold out';
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';

    this.priceEl = this.querySelector('[data-pdp-price]') || document.querySelector('[data-pdp-price]');

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #readVariants() {
    const tag = this.querySelector('script[type="application/json"][data-variants]');
    if (!tag) return [];
    try {
      const data = JSON.parse(tag.textContent);
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  }

  #onClick = (event) => {
    const btn = event.target.closest('[data-option-value]');
    if (!btn || btn.disabled) return;
    event.preventDefault();

    const position = btn.getAttribute('data-option-position');
    const value = btn.getAttribute('data-value');

    // Mark the clicked button selected within its group.
    const group = btn.closest('[data-option-group]');
    if (group) {
      group.querySelectorAll('[data-option-value]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    this.#update();
  };

  /** Read the currently-selected option value at each option position. */
  #selectedOptions() {
    const groups = this.querySelectorAll('[data-option-group]');
    const selected = [];
    groups.forEach((group) => {
      const pos = parseInt(group.getAttribute('data-option-position'), 10);
      const active = group.querySelector('[data-option-value].is-selected');
      if (active) selected[pos - 1] = active.getAttribute('data-value');
    });
    return selected;
  }

  /** Find the variant whose options match every selected option value. */
  #matchVariant() {
    if (this.variants.length === 1) return this.variants[0];
    const selected = this.#selectedOptions();
    return (
      this.variants.find((v) => {
        const opts = v.options || [];
        return selected.every((val, i) => val == null || opts[i] === val);
      }) || this.variants.find((v) => v.available) || this.variants[0]
    );
  }

  #update() {
    const variant = this.#matchVariant();
    if (!variant) return;

    // ?variant= URL (replaceState — no navigation, no scroll reset).
    const url = new URL(window.location.href);
    const numericId = String(variant.id).split('/').pop();
    url.searchParams.set('variant', numericId);
    window.history.replaceState({}, '', url);

    const available = Boolean(variant.available);

    // Price line.
    if (this.priceEl) {
      const money = this.#formatMoney(variant.price);
      let html = `<span>${money}</span>`;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        html = `<span>${money}</span><s style="opacity:0.45">${this.#formatMoney(
          variant.compare_at_price,
        )}</s>`;
      }
      const inner = this.priceEl.querySelector('.price');
      if (inner) inner.innerHTML = html;
    }

    // Add-to-cart controls (this form + the sticky bar both read [data-add-to-cart]).
    const label = this.preorder ? this.labelReserve : available ? this.labelAdd : this.labelSoldOut;
    const disabled = !this.preorder && !available;

    document.querySelectorAll('[data-add-to-cart]').forEach((form) => {
      const idInput = form.querySelector('[data-variant-id]');
      if (idInput) idInput.value = variant.id;
      const button = form.querySelector('[data-atc-button]');
      if (button) {
        button.textContent = label;
        button.disabled = disabled;
      }
    });

    // Express checkout form (payment_button) — keep its variant id in sync so the
    // wallet (Shop Pay / Apple Pay / …) reserves the option the shopper picked.
    document.querySelectorAll('[data-express-variant-id]').forEach((input) => {
      input.value = variant.id;
    });

    // Sticky-bar price mirror.
    document.querySelectorAll('[data-pdp-bar-price]').forEach((el) => {
      el.textContent = this.#formatMoney(variant.price);
    });

    // Let other modules (cart, sticky bar) react to the variant change.
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {variant, available, label, disabled},
      }),
    );
  }

  /**
   * Format integer cents (Liquid's `variant.price` JSON) through the shop's
   * `money_format` template, so a switched variant matches the server-rendered
   * `{{ ... | money }}` output. Supports the common amount placeholders.
   */
  #formatMoney(cents) {
    if (cents == null) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    const value = Number(cents) / 100;
    const withDecimals = (sep) =>
      value
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, sep === ',' ? '.' : ',')
        .replace('.', sep === ',' ? ',' : '.');
    const noDecimals = () =>
      Math.round(value)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount':
          return withDecimals('.');
        case 'amount_with_comma_separator':
          return withDecimals(',');
        case 'amount_no_decimals':
          return noDecimals();
        case 'amount_no_decimals_with_comma_separator':
          return Math.round(value)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        default:
          return withDecimals('.');
      }
    });
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
